/**
 * AiChatPanel — right-side collapsible slide-over panel for the AI assistant.
 *
 * Wider (480px), with a plain hairline header.
 * No thread sidebar — panel is for quick contextual chats.
 * AiChatRuntime wraps only the chat so the panel shell stays stable.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AiChat } from './AiChat';
import { AiChatRuntime } from './AiChatRuntime';
import { useAiChatPanel } from './AiChatProvider';

const PANEL_WIDTH = 480;

export function AiChatPanel() {
  const { panelOpen, closePanel } = useAiChatPanel();
  const navigate = useNavigate();

  const handleExpand = () => {
    closePanel();
    navigate('/ai-chat');
  };

  return (
    <AnimatePresence>
      {panelOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-zinc-950/10 z-40"
            onClick={closePanel}
          />

          {/* Panel */}
          <motion.aside
            initial={{ x: PANEL_WIDTH }}
            animate={{ x: 0 }}
            exit={{ x: PANEL_WIDTH }}
            transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
            style={{ width: PANEL_WIDTH }}
            className="fixed top-0 right-0 bottom-0 z-50 bg-white shadow-lg flex flex-col border-l border-zinc-200"
          >
            {/* Header */}
            <div className="flex h-12 items-center justify-between px-4 border-b border-zinc-200 bg-white shrink-0">
              <h2 className="text-sm font-medium text-zinc-900">Assistant</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleExpand}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors"
                  title="Open full page"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={closePanel}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Chat — inside the runtime */}
            <div className="flex-1 min-h-0 relative">
              <AiChatRuntime ephemeral>
                <AiChat className="h-full" />
              </AiChatRuntime>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
